/**
 * LegalPage — shared layout for legal & policy stubs.
 *
 * Per product-copy spec §10:
 *   - plain-English summary at the top
 *   - effective date + version
 *   - "draft pending counsel review" banner where applicable
 *   - footer contact: privacy@alouvya.com
 */
import MarketingNav from "./MarketingNav";
import MarketingFooter from "./MarketingFooter";
import MetaTags from "./MetaTags";

export default function LegalPage({
  title,
  metaTitle,
  metaDescription,
  effectiveDate = "20 February 2026",
  version = "v0.1 — draft",
  summary,
  children,
  testid,
}) {
  return (
    <div data-testid={testid} className="min-h-screen bg-white">
      <MetaTags title={metaTitle || `${title} — Alouvya`} description={metaDescription} />
      <MarketingNav />
      <main className="pt-32 sm:pt-40 pb-24">
        <article className="mx-auto max-w-3xl px-6 sm:px-10">
          <span className="eyebrow">Legal</span>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight leading-[1.05] text-[#1A1B2E]">
            {title}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-[#6B7280]">
            <span>Effective {effectiveDate}</span>
            <span aria-hidden>·</span>
            <span>{version}</span>
          </div>

          <div className="mt-8 rounded-2xl border border-[#FFB800]/30 bg-[#FFF4D6]/60 px-5 py-4 text-sm text-[#86601A]">
            This page is a working draft pending counsel review. Final language
            for legal use will be confirmed before launch.
          </div>

          {summary && (
            <section className="mt-10">
              <div className="eyebrow">Summary</div>
              <p className="mt-3 text-lg text-[#1A1B2E] leading-relaxed font-light">
                {summary}
              </p>
            </section>
          )}

          <section className="mt-12 text-[#1A1B2E] leading-relaxed [&_h2]:font-display [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:text-[#1A1B2E] [&_h2]:tracking-tight [&_h2]:mt-10 [&_h2]:mb-3 [&_p]:text-[#1A1B2E] [&_p]:mb-5 [&_p]:leading-relaxed [&_ul]:my-5 [&_ul]:list-disc [&_ul]:pl-6 [&_li]:mb-2 [&_li]:text-[#1A1B2E] [&_li]:leading-relaxed [&_strong]:font-semibold [&_strong]:text-[#1A1B2E] [&_a]:text-[#1F3A2A] [&_a]:underline [&_a]:underline-offset-4">
            {children}
          </section>

          <section className="mt-16 rounded-2xl bg-[#F7F8FC] border border-[#EEF0F4] p-6">
            <div className="eyebrow">Data requests &amp; questions</div>
            <p className="mt-3 text-sm text-[#1A1B2E]">
              Write to{" "}
              <a
                href="mailto:privacy@alouvya.com"
                className="text-[#1F3A2A] font-medium underline underline-offset-4"
              >
                privacy@alouvya.com
              </a>
              . We reply within one business day.
            </p>
          </section>
        </article>
      </main>
      <MarketingFooter />
    </div>
  );
}
